function clamp(value, min = 0, max = 1) {
	return Math.min(max, Math.max(min, value));
}

function toStableTokens(rawValue) {
	if (typeof rawValue !== 'string' || rawValue.length === 0) {
		return [];
	}

	return rawValue
		.toLowerCase()
		.split(/[^a-z0-9_-]+/)
		.map((token) => token.trim())
		.filter((token) => token.length >= 3 && !/\d{3,}/.test(token));
}

function getNodeDepth(node) {
	let depth = 0;
	let current = node?.parentElement || null;

	while (current) {
		depth += 1;
		current = current.parentElement;
	}

	return depth;
}

function buildStableSelector(node) {
	if (!node) {
		return '';
	}

	const tagName = node.tagName.toLowerCase();
	const idTokens = toStableTokens(node.id || '');

	if (idTokens.length > 0 && node.id && !/^\d+$/.test(node.id)) {
		return `#${node.id}`;
	}

	const classTokens = Array.from(node.classList || [])
		.flatMap((token) => toStableTokens(token))
		.slice(0, 3);

	if (classTokens.length > 0) {
		return `${tagName}.${classTokens.join('.')}`;
	}

	return tagName;
}

function buildFingerprint(node, type) {
	if (!node) {
		return `${type}|unknown`;
	}

	const segments = [];
	let current = node;
	let limit = 0;

	while (current && limit < 5) {
		if (!current.tagName) {
			break;
		}

		const tagName = current.tagName.toLowerCase();
		const idToken = toStableTokens(current.id || '')[0] || '';
		const classToken = Array.from(current.classList || [])
			.flatMap((token) => toStableTokens(token))
			.find((token) => token.length > 0) || '';
		const tokenPart = idToken || classToken;

		segments.unshift(tokenPart ? `${tagName}.${tokenPart}` : tagName);
		current = current.parentElement;
		limit += 1;
	}

	return `${type}|${segments.join('>')}`;
}

export class CommonLayoutDetector {
	constructor() {
		this.headerTokens = ['header', 'masthead', 'topbar', 'navbar', 'navigation', 'site-header', 'main-header'];
		this.footerTokens = ['footer', 'colophon', 'site-footer', 'main-footer', 'bottom', 'copyright'];
		this.noiseTokens = ['cookie', 'consent', 'modal', 'popup', 'notice', 'alert', 'banner-ad', 'subscribe'];
	}

	detectPageLayout(html) {
		if (typeof html !== 'string' || html.length === 0) {
			return {
				header: null,
				footer: null
			};
		}

		try {
			const parser = new DOMParser();
			const documentNode = parser.parseFromString(html, 'text/html');
			const allElements = Array.from(documentNode.querySelectorAll('body *'));

			if (allElements.length === 0) {
				return {
					header: null,
					footer: null
				};
			}

			const headerCandidates = this.collectCandidates(documentNode, allElements, 'header');
			const footerCandidates = this.collectCandidates(documentNode, allElements, 'footer');

			return {
				header: headerCandidates[0] || null,
				footer: footerCandidates[0] || null
			};
		} catch {
			return {
				header: null,
				footer: null
			};
		}
	}

	collectCandidates(documentNode, allElements, type) {
		const selectorCandidates = type === 'header'
			? [
				'header',
				'[role="banner"]',
				'#header',
				'#masthead',
				'.site-header',
				'.main-header',
				'.masthead',
				'nav'
			]
			: [
				'footer',
				'[role="contentinfo"]',
				'#footer',
				'#colophon',
				'.site-footer',
				'.main-footer',
				'.colophon'
			];

		const seenNodes = new Set();
		const candidates = [];

		for (const selector of selectorCandidates) {
			const nodes = Array.from(documentNode.querySelectorAll(selector));

			for (const node of nodes) {
				if (seenNodes.has(node)) {
					continue;
				}

				seenNodes.add(node);
				const scored = this.scoreCandidate(node, allElements, type);

				if (scored.score <= 0) {
					continue;
				}

				candidates.push(scored);
			}
		}

		return candidates.sort((left, right) => right.score - left.score);
	}

	scoreCandidate(node, allElements, type) {
		const tagName = node.tagName.toLowerCase();
		const role = (node.getAttribute('role') || '').toLowerCase();
		const classTokens = toStableTokens(node.className || '');
		const idTokens = toStableTokens(node.id || '');
		const combinedTokens = [...classTokens, ...idTokens];
		const tokenSet = new Set(combinedTokens);
		const nodeIndex = Math.max(0, allElements.indexOf(node));
		const positionRatio = allElements.length > 0 ? nodeIndex / allElements.length : 0;
		const linkCount = node.querySelectorAll('a[href]').length;
		const textLength = (node.textContent || '').trim().length;
		const depth = getNodeDepth(node);
		const tokenList = type === 'header' ? this.headerTokens : this.footerTokens;

		let score = 0;

		if (type === 'header') {
			if (tagName === 'header') {
				score += 4;
			}

			if (role === 'banner') {
				score += 4;
			}

			if (positionRatio <= 0.25) {
				score += 3;
			} else if (positionRatio <= 0.5) {
				score += 1;
			} else {
				score -= 2;
			}
		} else {
			if (tagName === 'footer') {
				score += 4;
			}

			if (role === 'contentinfo') {
				score += 4;
			}

			if (positionRatio >= 0.7) {
				score += 3;
			} else if (positionRatio >= 0.5) {
				score += 1;
			} else {
				score -= 2;
			}
		}

		for (const token of tokenList) {
			if (tokenSet.has(token) || combinedTokens.some((existingToken) => existingToken.includes(token))) {
				score += 2;
			}
		}

		if (node.querySelector('nav')) {
			score += type === 'header' ? 2 : 1;
		}

		if (linkCount >= 3) {
			score += 1;
		}

		if (textLength < 20) {
			score -= 1;
		}

		if (depth > 8) {
			score -= 1;
		}

		if (this.noiseTokens.some((token) => tokenSet.has(token) || combinedTokens.some((item) => item.includes(token)))) {
			score -= 5;
		}

		return {
			type,
			score,
			selector: buildStableSelector(node),
			fingerprint: buildFingerprint(node, type)
		};
	}

	computeConsensus(pages, minimumRatio = 0.6) {
		const safePages = Array.isArray(pages) ? pages : [];
		const totalPages = safePages.length;
		const consensus = {
			totalPages,
			header: this.computeTypeConsensus(safePages, 'header', minimumRatio),
			footer: this.computeTypeConsensus(safePages, 'footer', minimumRatio)
		};

		return consensus;
	}

	computeTypeConsensus(pages, type, minimumRatio) {
		const map = new Map();
		let consideredPages = 0;

		for (const page of pages) {
			const candidate = page?.layoutSignals?.[type] || null;

			if (!candidate || !candidate.fingerprint) {
				continue;
			}

			consideredPages += 1;
			const existing = map.get(candidate.fingerprint) || {
				count: 0,
				totalScore: 0,
				selector: candidate.selector,
				fingerprint: candidate.fingerprint
			};

			existing.count += 1;
			existing.totalScore += candidate.score;
			map.set(candidate.fingerprint, existing);
		}

		if (map.size === 0 || pages.length === 0) {
			return {
				detected: false,
				selector: '',
				fingerprint: '',
				confidence: 0,
				presenceRatio: 0,
				matchedPages: 0
			};
		}

		const entries = Array.from(map.values()).sort((left, right) => {
			if (right.count !== left.count) {
				return right.count - left.count;
			}

			return right.totalScore - left.totalScore;
		});
		const winner = entries[0];
		const presenceRatio = winner.count / pages.length;
		const averageScore = winner.totalScore / winner.count;
		const confidence = clamp((presenceRatio * 0.7) + (clamp(averageScore / 12) * 0.3));
		const detected = presenceRatio >= minimumRatio;

		return {
			detected,
			selector: detected ? winner.selector : '',
			fingerprint: detected ? winner.fingerprint : '',
			confidence,
			presenceRatio,
			matchedPages: winner.count,
			consideredPages
		};
	}
}
